import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { Mail, Linkedin, Globe, ArrowRight } from 'lucide-react';

// Inicialização do Supabase usando variáveis de ambiente
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL!,
  import.meta.env.VITE_SUPABASE_ANON_KEY!
);

type TeamMember = {
  id: string;
  name: string;
  role: string;
  description: string;
  image: string;
  email?: string;
  linkedin?: string;
  website?: string;
};

const Team = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        let { data, error } = await supabase.from('team_members').select('*');
        if (error) throw error;
        
        // Process image URLs
        const processedMembers = (data || []).map(member => {
          if (member.image && !member.image.startsWith('http')) {
            const { data: { publicUrl } } = supabase.storage
              .from('team')
              .getPublicUrl(member.image);
            return { ...member, image: publicUrl };
          }
          return member;
        });
        
        setTeamMembers(processedMembers);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTeamMembers();
  }, []);

  return (
    <div className="min-h-screen pt-16">
      {/* Hero Section */}
      <div className="relative h-[60vh] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.6)), url("https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80")',
            backgroundAttachment: 'fixed'
          }}
        />
        <div className="relative h-full flex items-center justify-center text-center px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-4xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">A Nossa Equipa</h1>
            <p className="text-xl md:text-2xl text-gray-300">Conheça os profissionais dedicados que fazem o projeto SINOPSE acontecer.</p>
          </motion.div>
        </div>
      </div>

      {/* Team Section */}
      <div className="py-20 bg-gradient-to-b from-background via-secondary/20 to-background">
        <div className="max-w-7xl mx-auto px-4">
          {loading && <p className="text-center text-gray-400">Carregando...</p>}
          {error && <p className="text-center text-red-500">{error}</p>}
          {!loading && !error && (
            <motion.div ref={ref} initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ duration: 0.5 }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {teamMembers.map((member, index) => (
                <motion.div 
                  key={member.id} 
                  initial={{ opacity: 0, y: 20 }} 
                  animate={inView ? { opacity: 1, y: 0 } : {}} 
                  transition={{ delay: index * 0.1 }} 
                  className="group relative"
                >
                  <div className="relative h-[400px] rounded-2xl overflow-hidden bg-secondary/50">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      style={{
                        objectFit: 'cover',
                        objectPosition: 'center'
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
                      {member.email && (
                        <a href={`mailto:${member.email}`} className="p-2 bg-background/80 backdrop-blur-sm rounded-full hover:bg-primary/90 transition-colors" title="Email" onClick={e => e.stopPropagation()}>
                          <Mail className="w-5 h-5" />
                        </a>
                      )}
                      {member.linkedin && (
                        <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="p-2 bg-background/80 backdrop-blur-sm rounded-full hover:bg-primary/90 transition-colors" title="LinkedIn" onClick={e => e.stopPropagation()}>
                          <Linkedin className="w-5 h-5" />
                        </a>
                      )}
                      {member.website && (
                        <a href={member.website} target="_blank" rel="noopener noreferrer" className="p-2 bg-background/80 backdrop-blur-sm rounded-full hover:bg-primary/90 transition-colors" title="Website" onClick={e => e.stopPropagation()}>
                          <Globe className="w-5 h-5" />
                        </a>
                      )}
                    </div>
                  </div>
                  <Link to={`/team/${member.id}`} className="block mt-4">
                    <h3 className="text-xl font-semibold group-hover:text-primary transition-colors">{member.name}</h3>
                    <p className="text-primary/80 text-sm mb-2">{member.role}</p>
                    <p className="text-gray-400 text-sm line-clamp-3">{member.description}</p>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </div>

      {/* Call to Action */}
      <div className="py-20 bg-gradient-to-b from-secondary/20 to-background">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-card rounded-2xl p-8 md:p-12">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-4">Junte-se à Nossa Equipa</h2>
              <p className="text-gray-400 mb-8">Estamos sempre à procura de professores e profissionais apaixonados por educação e inovação. Se você quer fazer parte desta equipa incrível, entre em contacto connosco.</p>
              <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-3 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-colors group">
                Entre em Contacto
                <motion.span initial={{ x: 0 }} whileHover={{ x: 5 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}>
                  <ArrowRight className="w-5 h-5" />
                </motion.span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Team;